/*
Season store — in-memory observable for the active season selection.

Mirrors Android SeasonStore.kt: NOT persisted across reloads. The default
state is `current()` so a fresh page load always shows the live season's
captures, and entering archive mode is an explicit per-session choice.

API:
- read():            sync access to the current ordinal
- subscribe(fn):     observer registration; fn fires immediately + on every change
- set(ord):          mutate; notifies all subscribers if the value changed

Design notes:
- A tiny subscriber set is enough — we have at most a handful of mounted
  screens at any moment. No need for an external library.
- Subscribers must clean up on screen unmount; the returned `unsubscribe`
  closure removes them in O(1).
*/

import { seasonFromTimestamp } from './season'
import type { SeasonOrdinal } from './types'

export const kSeasonOrdinals: SeasonOrdinal[] = [0, 1, 2, 3]
export const kSeasonNames: Record<SeasonOrdinal, string> = {
	0: 'Hiver',
	1: 'Printemps',
	2: 'Été',
	3: 'Automne',
}

let fSelected: SeasonOrdinal = seasonFromTimestamp(Date.now())  // active selection, defaults to live season
const fSubscribers = new Set<(inSeason: SeasonOrdinal) => void>()  // observers, removed on unsubscribe

/*
Read the current selection synchronously. Use this for one-shot reads
(e.g. a screen that doesn't need to react to changes).
*/
export function getSeason(): SeasonOrdinal { return fSelected }

/*
Set the selection. No-op if unchanged. Notifies all subscribers
synchronously after the value swap.
*/
export function setSeason(inSeason: SeasonOrdinal): void
{
	if (inSeason === fSelected) return
	fSelected = inSeason
	for (const vFn of fSubscribers) vFn(inSeason)
}

/*
Subscribe to changes. The callback fires once immediately with the
current value, then again on every set() that actually changes the
state. Returns an unsubscribe closure — call it on screen unmount to
avoid stale handlers updating detached DOM.
*/
export function subscribeSeason(inFn: (inSeason: SeasonOrdinal) => void): () => void
{
	fSubscribers.add(inFn)
	inFn(fSelected)  // emit current value so the subscriber renders without a separate read
	return () => { fSubscribers.delete(inFn) }
}

/*
Live season (computed at call time from the device clock, not cached).
The Arboretum + Remarquables banners use this to detect archive mode.
*/
export function liveSeason(): SeasonOrdinal { return seasonFromTimestamp(Date.now()) }
